import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Camera calibration coverage of a range for the referee pickers (user
 * request 2026-11-01): returns a predicate "this target has no camera
 * calibration here". Always false while loading, on errors and on ranges
 * without a camera — the mark is a hint, never a blocker.
 */
export function useUncalibrated(
  locationId: string | null | undefined,
): (targetTypeId: string | null | undefined) => boolean {
  const [calibrated, setCalibrated] = useState<Set<string> | null>(null);
  useEffect(() => {
    setCalibrated(null);
    if (!locationId) return;
    let cancelled = false;
    api
      .calibrationCoverage(locationId)
      .then((c) => {
        if (!cancelled && c?.hasCamera) setCalibrated(new Set(c.calibratedTargetTypeIds));
      })
      .catch((err: unknown) => console.warn('calibrationCoverage failed', err));
    return () => {
      cancelled = true;
    };
  }, [locationId]);
  return (targetTypeId) => calibrated != null && !!targetTypeId && !calibrated.has(targetTypeId);
}

/**
 * Running round: true when this range has a camera but the round's target
 * has no calibration (vision.statusForSession → needs_calibration). Drives
 * the amber banner on the scoring screen; checked when the assignment
 * changes and every minute (a calibration saved meanwhile clears it).
 */
export function useRoundNeedsCalibration(
  sessionId: string | null | undefined,
  assignmentId: string | null | undefined,
): { needed: boolean; targetName: string | null } {
  const [state, setState] = useState<{ needed: boolean; targetName: string | null }>({
    needed: false,
    targetName: null,
  });
  useEffect(() => {
    setState({ needed: false, targetName: null });
    if (!sessionId || !assignmentId) return;
    let cancelled = false;
    const check = () =>
      api
        .visionStatus(sessionId, assignmentId)
        .then((s) => {
          if (cancelled) return;
          setState({
            needed: s?.state === 'needs_calibration',
            targetName: s?.targetName ?? null,
          });
        })
        .catch(() => {
          // offline / no permission: no banner (hint only)
        });
    void check();
    const t = window.setInterval(() => void check(), 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [sessionId, assignmentId]);
  return state;
}
